import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Home, HeartPulse, FileText, ArrowRight, ClipboardList, FolderLock, BellRing,
} from 'lucide-react';
import { policiesApi, quotesApi, documentsApi, renewalsApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';

export default function DashboardHome() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ policies: 0, quotes: 0, documents: 0, renewals: 0 });

  useEffect(() => {
    Promise.all([
      policiesApi.list().catch(() => []),
      quotesApi.list().catch(() => []),
      documentsApi.list().catch(() => []),
      renewalsApi.list().catch(() => []),
    ]).then(([p, q, d, r]) => {
      setStats({ policies: p.length, quotes: q.length, documents: d.length, renewals: r.length });
    });
  }, []);

  return (
    <div>
      <div className="mb-6">
        <span className="eyebrow">Dashboard</span>
        <h1 className="mt-2 text-3xl font-bold text-[#0b1f3a]">Welcome back, {user?.name}</h1>
        <p className="text-slate-600 text-sm mt-1">Everything about your Spanish insurance, all in one place.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatBox icon={FileText}      label="Active policies" value={stats.policies} />
        <StatBox icon={ClipboardList} label="Saved quotes"    value={stats.quotes} />
        <StatBox icon={FolderLock}    label="Documents"       value={stats.documents} />
        <StatBox icon={BellRing}      label="Upcoming renewals" value={stats.renewals} accent={stats.renewals > 0} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mt-8">
        <QuickCard icon={Home}       title="New home insurance quote"    text="Compare policies for another property."  to="/home-insurance" />
        <QuickCard icon={HeartPulse} title="New health insurance quote"  text="Compare private health cover plans."     to="/health-insurance" />
        <QuickCard icon={FolderLock} title="Upload documents"            text="Modelo 210, IBI, deeds, policies and more." to="/dashboard/documents" />
      </div>

      <div className="mt-8 bg-white border border-slate-200 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-semibold text-[#0b1f3a]">Getting started</h2>
        </div>
        <ol className="space-y-3 text-sm text-slate-700">
          <li className="flex gap-3"><Dot n="1" /> Complete your first quote to compare insurers.</li>
          <li className="flex gap-3"><Dot n="2" /> Upload your title deed, IBI and current policies to the Documents vault.</li>
          <li className="flex gap-3"><Dot n="3" /> We will email you 60, 30, 14 and 7 days before your renewal.</li>
        </ol>
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, accent }) {
  return (
    <div className={`bg-white border rounded-2xl p-5 ${accent ? 'border-amber-300' : 'border-slate-200'}`}>
      <span className="icon-tile mb-3"><Icon size={18} /></span>
      <p className="text-2xl font-bold text-[#0b1f3a]">{value}</p>
      <p className="text-xs text-slate-500 mt-0.5">{label}</p>
    </div>
  );
}

function QuickCard({ icon: Icon, title, text, to }) {
  return (
    <Link to={to} className="bg-white border border-slate-200 rounded-2xl p-6 card-lift block">
      <span className="icon-tile mb-4"><Icon size={20} /></span>
      <h3 className="font-semibold text-[#0b1f3a]">{title}</h3>
      <p className="text-sm text-slate-600 mt-1.5">{text}</p>
      <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#0d3a8a]">Continue <ArrowRight size={14} /></span>
    </Link>
  );
}

function Dot({ n }) {
  return <span className="w-6 h-6 rounded-full bg-[#eef4fb] text-[#0d3a8a] inline-flex items-center justify-center text-xs font-bold shrink-0">{n}</span>;
}
